// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// Copyright 2026 Amantus Machina GmbH

'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import type { PostHogProvider as PosthogReactProvider } from 'posthog-js/react';
import { type ReactNode, useEffect, useState } from 'react';
import {
  ensurePosthogInitialized,
  getPosthogClient,
  type PosthogClient,
  shouldInitializePosthog,
} from '@/lib/observability/posthog';
import { composeBrowserUrl } from '@/lib/utils/runtime';

type PosthogRuntime = {
  client: PosthogClient;
  Provider: typeof PosthogReactProvider;
};

export function PostHogPageView(): null {
  const pathname = usePathname();
  const searchParameters = useSearchParams();
  const shouldInit = shouldInitializePosthog();

  useEffect(() => {
    if (!(shouldInit && pathname)) {
      return;
    }

    const abortController = new AbortController();

    const track = async () => {
      await ensurePosthogInitialized();
      if (abortController.signal.aborted) {
        return;
      }

      const client = await getPosthogClient();
      if (abortController.signal.aborted) {
        return;
      }

      const search = searchParameters?.toString() ?? null;
      const url = composeBrowserUrl(pathname, search);
      if (!url) {
        return;
      }

      client.capture('$pageview', {
        $current_url: url,
      });
    };

    track().catch((error: unknown) => {
      console.error('Failed to capture PostHog page view', error);
    });

    return () => {
      abortController.abort();
    };
  }, [shouldInit, pathname, searchParameters]);

  return null;
}

interface PostHogProviderProperties {
  children: ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProperties) {
  const shouldInit = shouldInitializePosthog();
  const [runtime, setRuntime] = useState<PosthogRuntime | null>(null);

  useEffect(() => {
    if (!shouldInit) {
      return;
    }

    const abortController = new AbortController();

    const load = async () => {
      await ensurePosthogInitialized();
      if (abortController.signal.aborted) {
        return;
      }

      const [client, reactModule] = await Promise.all([getPosthogClient(), import('posthog-js/react')]);

      if (abortController.signal.aborted) {
        return;
      }

      setRuntime({ client, Provider: reactModule.PostHogProvider });
    };

    load().catch((error: unknown) => {
      console.error('Failed to load PostHog runtime', error);
    });

    return () => {
      abortController.abort();
    };
  }, [shouldInit]);

  if (!(shouldInit && runtime)) {
    return <>{children}</>;
  }

  const ProviderComponent = runtime.Provider;
  return <ProviderComponent client={runtime.client}>{children}</ProviderComponent>;
}
